<script setup lang="ts">
import { ref } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/Layouts/AppLayout.vue'
import { CheckCircle, XCircle, Calendar, User } from 'lucide-vue-next'

interface Subscription {
  id: number
  status: string
  student_name: string
  tier_name: string
  amount: number
  session_day: string | null
  session_time: string | null
}

interface Props {
  decision: 'accepted' | 'rejected'
  subscription: Subscription
  acceptUrl: string
  rejectUrl: string
}

const props = defineProps<Props>()

const choice = ref<'accepted' | 'rejected'>(props.decision)
const isSubmitting = ref(false)

function submit() {
  if (isSubmitting.value) return
  isSubmitting.value = true

  router.post(choice.value === 'accepted' ? props.acceptUrl : props.rejectUrl, {}, {
    onFinish: () => { isSubmitting.value = false },
  })
}
</script>

<template>
  <AppLayout>
    <main class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 py-12">
      <div class="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        <h1 class="text-2xl font-bold text-gray-900 mb-2 text-center">Confirm your decision</h1>
        <p class="text-gray-600 mb-6 text-center">
          Review the request below, then confirm. Nothing has happened yet.
        </p>

        <div class="bg-gray-50 rounded-xl p-4 border border-gray-200 mb-6">
          <div class="flex items-center gap-2 text-sm text-gray-700 mb-2">
            <User class="w-4 h-4 text-gray-400" />
            <span class="font-semibold">{{ subscription.student_name }}</span>
          </div>
          <div class="flex justify-between text-sm mb-1">
            <span class="text-gray-500">Plan</span>
            <span class="font-medium text-gray-900">{{ subscription.tier_name }}</span>
          </div>
          <div class="flex justify-between text-sm">
            <span class="text-gray-500">Weekly cost</span>
            <span class="font-medium text-gray-900">£{{ subscription.amount.toFixed(2) }}</span>
          </div>
          <div v-if="subscription.session_day" class="flex items-center gap-2 text-sm text-gray-600 mt-3 pt-3 border-t border-gray-200">
            <Calendar class="w-4 h-4 text-gray-400" />
            <span>{{ subscription.session_day }} at {{ subscription.session_time }}</span>
          </div>
        </div>

        <div class="grid grid-cols-2 gap-3 mb-6">
          <button
            type="button"
            @click="choice = 'accepted'"
            :class="[
              'flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-semibold transition-colors',
              choice === 'accepted'
                ? 'border-green-600 bg-green-50 text-green-700'
                : 'border-gray-200 text-gray-500 hover:border-gray-300',
            ]"
          >
            <CheckCircle class="w-5 h-5" />
            Accept
          </button>
          <button
            type="button"
            @click="choice = 'rejected'"
            :class="[
              'flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-semibold transition-colors',
              choice === 'rejected'
                ? 'border-red-600 bg-red-50 text-red-700'
                : 'border-gray-200 text-gray-500 hover:border-gray-300',
            ]"
          >
            <XCircle class="w-5 h-5" />
            Decline
          </button>
        </div>

        <p class="text-sm text-gray-500 mb-4 text-center">
          <template v-if="choice === 'accepted'">
            {{ subscription.student_name }}'s card will be charged £{{ subscription.amount.toFixed(2) }} for the first week and their subscription will start.
          </template>
          <template v-else>
            The payment authorization will be released. {{ subscription.student_name }} will not be charged.
          </template>
        </p>

        <button
          @click="submit"
          :disabled="isSubmitting"
          class="w-full py-3 bg-gradient-to-r from-teal-500 to-teal-800 text-white rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all disabled:opacity-60"
        >
          {{ isSubmitting ? 'Processing…' : (choice === 'accepted' ? 'Confirm acceptance' : 'Confirm decline') }}
        </button>
      </div>
    </main>
  </AppLayout>
</template>
